import {
  RELATED_BOUNDS,
  RELATED_PERCENTILE,
  VARIANT_BOUNDS,
  VARIANT_PERCENTILE,
  adaptiveSimilarityThreshold,
} from './thresholds';
import type {
  FeedbackBreakdown,
  FeedbackNeighbor,
  Paper,
  ProfileItem,
  RankingResult,
  ReviewDecision,
} from './types';

export type Vector = readonly number[];

export interface EmbeddedProfileItem {
  item: ProfileItem;
  embedding: Vector;
  bodyEmbeddings?: readonly WeightedEmbedding[];
}

export interface EmbeddedPaper {
  paper: Paper;
  embedding: Vector;
}

export interface WeightedEmbedding {
  embedding: Vector;
  weight: number;
}

export interface RankingOptions {
  profileMatchCount?: number;
  summaryWeight?: number;
  bodyWeight?: number;
  bodyMatchCount?: number;
  explanationCount?: number;
}

export interface FeedbackOptions {
  positiveWeight?: number;
  negativeWeight?: number;
  keywordWeight?: number;
  neighborCount?: number;
  papers?: readonly Pick<Paper, 'id' | 'topics'>[];
}

const DEFAULT_PROFILE_MATCH_COUNT = 3;
const PROFILE_MATCH_RATIO = 0.8;
const PROFILE_MATCH_DECAY = 0.5;
const SAME_AREA_PROFILE_COSINE = 0.5;
const SECOND_AREA_COVERAGE_WEIGHT = 0.4;
const DEFAULT_SUMMARY_WEIGHT = 0.7;
const DEFAULT_BODY_WEIGHT = 0.3;
const DEFAULT_BODY_MATCH_COUNT = 3;
const DEFAULT_EXPLANATION_COUNT = 3;
export const MAX_STORED_BODY_CHUNKS = 24;
export const INTERESTED_PAPER_BOOST_THRESHOLD = 0.5;
export const SIMILAR_PAPER_PENALTY_THRESHOLD = 0.8;
export const KEYWORD_PENALTY_THRESHOLD = 0.45;
export const DENSE_RERANK_WEIGHT = 0.7;
export const QWEN3_RERANK_WEIGHT = 0.3;

export function blendRerankerScore(denseScore: number, rerankerScore: number): number {
  return denseScore * DENSE_RERANK_WEIGHT + rerankerScore * QWEN3_RERANK_WEIGHT;
}

function minMaxNormalize(values: readonly number[]): number[] {
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  if (span <= 1e-12) return values.map(() => 0.5);
  return values.map((value) => (value - min) / span);
}

/**
 * Mixes dense cosine and Qwen3 scores after calibrating each within the
 * reranked window. Raw 70/30 mixing is misleading because cosine and yes/no
 * probabilities live on different scales; a weakly calibrated reranker would
 * otherwise shrink every top candidate toward zero. Uniform reranker scores
 * leave the dense order unchanged. The blend is mapped back into the window's
 * dense score range so the result remains comparable to unreranked cosine.
 */
export function blendCalibratedRerankerScores(
  candidates: readonly { denseScore: number; rerankerScore: number }[],
): number[] {
  if (candidates.length === 0) return [];

  const denseScores = candidates.map(({ denseScore }) => denseScore);
  const rerankerScores = candidates.map(({ rerankerScore }) => rerankerScore);
  const denseNorm = minMaxNormalize(denseScores);
  const rerankMin = Math.min(...rerankerScores);
  const rerankMax = Math.max(...rerankerScores);
  const rerankNorm =
    rerankMax - rerankMin <= 1e-12 ? denseNorm : minMaxNormalize(rerankerScores);
  const blendedNorm = denseNorm.map(
    (value, index) =>
      DENSE_RERANK_WEIGHT * value + QWEN3_RERANK_WEIGHT * (rerankNorm[index] ?? value),
  );
  const denseMin = Math.min(...denseScores);
  const denseMax = Math.max(...denseScores);
  const denseSpan = denseMax - denseMin;
  if (denseSpan <= 1e-12) return [...denseScores];
  return blendedNorm.map((value) => denseMin + value * denseSpan);
}

/**
 * Keeps unreranked papers below the reranked window while preserving their
 * relative dense ranking instead of collapsing them onto an epsilon staircase.
 */
export function placeUnrerankedAfter(
  rerankedTop: readonly RankingResult[],
  remaining: readonly RankingResult[],
): RankingResult[] {
  if (remaining.length === 0) return [...rerankedTop];
  const floor = Math.min(...rerankedTop.map(({ score }) => score), 0);
  const denseScores = remaining.map((ranking) => ranking.denseScore ?? ranking.score);
  const denseMax = Math.max(...denseScores);
  const denseMin = Math.min(...denseScores);
  const span = Math.max(1e-6, denseMax - denseMin);
  return [
    ...rerankedTop,
    ...remaining.map((ranking, index) => {
      const dense = ranking.denseScore ?? ranking.score;
      const relative = (denseMax - dense) / span;
      return {
        ...ranking,
        denseScore: dense,
        score: Math.max(-1, floor - 0.001 * relative - (index + 1) * 1e-9),
      };
    }),
  ];
}

function assertSameDimensions(left: Vector, right: Vector): void {
  if (left.length !== right.length) {
    throw new Error(`Vector dimensions differ: ${left.length} and ${right.length}`);
  }
}

export function normalizeVector(vector: Vector): number[] {
  let squaredMagnitude = 0;
  for (const value of vector) {
    squaredMagnitude += value * value;
  }

  if (squaredMagnitude === 0) {
    return vector.map(() => 0);
  }

  const magnitude = Math.sqrt(squaredMagnitude);
  return vector.map((value) => value / magnitude);
}

export function cosineSimilarity(left: Vector, right: Vector): number {
  assertSameDimensions(left, right);

  let dotProduct = 0;
  let leftSquaredMagnitude = 0;
  let rightSquaredMagnitude = 0;

  for (let index = 0; index < left.length; index += 1) {
    const leftValue = left[index] ?? 0;
    const rightValue = right[index] ?? 0;
    dotProduct += leftValue * rightValue;
    leftSquaredMagnitude += leftValue * leftValue;
    rightSquaredMagnitude += rightValue * rightValue;
  }

  const magnitudeProduct = Math.sqrt(leftSquaredMagnitude * rightSquaredMagnitude);
  return magnitudeProduct === 0 ? 0 : dotProduct / magnitudeProduct;
}

export function aggregateChunkEmbeddings(
  chunks: readonly Vector[],
  weights?: readonly number[],
): number[] {
  if (chunks.length === 0) {
    return [];
  }
  if (weights && weights.length !== chunks.length) {
    throw new Error(`Chunk and weight counts differ: ${chunks.length} and ${weights.length}`);
  }

  const dimensions = chunks[0]?.length ?? 0;
  const sum = new Array<number>(dimensions).fill(0);

  for (const [chunkIndex, chunk] of chunks.entries()) {
    if (chunk.length !== dimensions) {
      throw new Error(`Chunk dimensions differ: expected ${dimensions}, received ${chunk.length}`);
    }
    const weight = finiteNonNegative(weights?.[chunkIndex], 1);
    for (let index = 0; index < dimensions; index += 1) {
      sum[index] = (sum[index] ?? 0) + (chunk[index] ?? 0) * weight;
    }
  }

  return normalizeVector(sum);
}

function finiteNonNegative(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) && value >= 0 ? value : fallback;
}

function weightedMean(values: readonly { value: number; weight: number }[]): number {
  let sum = 0;
  let totalWeight = 0;
  for (const { value, weight } of values) {
    const safeWeight = finiteNonNegative(weight, 0);
    sum += value * safeWeight;
    totalWeight += safeWeight;
  }
  return totalWeight === 0 ? 0 : sum / totalWeight;
}

export function topBodyChunkSimilarity(
  paperEmbedding: Vector,
  bodyEmbeddings: readonly WeightedEmbedding[],
  count = DEFAULT_BODY_MATCH_COUNT,
): number | undefined {
  const matchCount = Math.max(0, Math.floor(finiteNonNegative(count, DEFAULT_BODY_MATCH_COUNT)));
  if (bodyEmbeddings.length === 0 || matchCount === 0) return undefined;

  const matches = bodyEmbeddings
    .filter(({ weight }) => Number.isFinite(weight) && weight > 0)
    .map(({ embedding, weight }) => ({
      value: cosineSimilarity(embedding, paperEmbedding),
      weight,
    }))
    .sort((left, right) => right.value - left.value)
    .slice(0, matchCount);
  return matches.length === 0 ? undefined : weightedMean(matches);
}

export function noisyOrCoverage(
  primary: number,
  secondary: number,
  coverageWeight = SECOND_AREA_COVERAGE_WEIGHT,
): number {
  const safePrimary = Math.min(1, Math.max(0, primary));
  const safeSecondary = Math.min(1, Math.max(0, secondary));
  const safeWeight = Math.min(1, Math.max(0, coverageWeight));
  return 1 - (1 - safePrimary) * (1 - safeWeight * safeSecondary);
}

function similaritiesToOthers(
  seedId: string,
  seed: Vector,
  embeddings: Readonly<Record<string, Vector>>,
): number[] {
  return Object.entries(embeddings).flatMap(([id, embedding]) =>
    id === seedId ? [] : [cosineSimilarity(seed, embedding)],
  );
}

export function relatedThresholdForSeed(
  seedId: string,
  seed: Vector,
  embeddings: Readonly<Record<string, Vector>>,
): number {
  return adaptiveSimilarityThreshold(
    similaritiesToOthers(seedId, seed, embeddings),
    RELATED_PERCENTILE,
    INTERESTED_PAPER_BOOST_THRESHOLD,
    RELATED_BOUNDS,
  );
}

export function variantThresholdForSeed(
  seedId: string,
  seed: Vector,
  embeddings: Readonly<Record<string, Vector>>,
): number {
  return adaptiveSimilarityThreshold(
    similaritiesToOthers(seedId, seed, embeddings),
    VARIANT_PERCENTILE,
    SIMILAR_PAPER_PENALTY_THRESHOLD,
    VARIANT_BOUNDS,
  );
}

export function relatedThresholdForPool(embeddings: Readonly<Record<string, Vector>>): number {
  const entries = Object.entries(embeddings);
  const similarities: number[] = [];
  for (let left = 0; left < entries.length; left += 1) {
    for (let right = left + 1; right < entries.length; right += 1) {
      const leftEmbedding = entries[left]?.[1];
      const rightEmbedding = entries[right]?.[1];
      if (!leftEmbedding || !rightEmbedding) continue;
      similarities.push(cosineSimilarity(leftEmbedding, rightEmbedding));
    }
  }
  return adaptiveSimilarityThreshold(
    similarities,
    RELATED_PERCENTILE,
    INTERESTED_PAPER_BOOST_THRESHOLD,
    RELATED_BOUNDS,
  );
}

export function variantThresholdForPool(embeddings: Readonly<Record<string, Vector>>): number {
  const entries = Object.entries(embeddings);
  const similarities: number[] = [];
  for (let left = 0; left < entries.length; left += 1) {
    for (let right = left + 1; right < entries.length; right += 1) {
      const leftEmbedding = entries[left]?.[1];
      const rightEmbedding = entries[right]?.[1];
      if (!leftEmbedding || !rightEmbedding) continue;
      similarities.push(cosineSimilarity(leftEmbedding, rightEmbedding));
    }
  }
  return adaptiveSimilarityThreshold(
    similarities,
    VARIANT_PERCENTILE,
    SIMILAR_PAPER_PENALTY_THRESHOLD,
    VARIANT_BOUNDS,
  );
}

function keywordThresholdForPhrase(
  embedding: Vector,
  paperEmbeddings: Readonly<Record<string, Vector>>,
): number {
  const similarities = Object.values(paperEmbeddings).map((paper) =>
    cosineSimilarity(embedding, paper),
  );
  return adaptiveSimilarityThreshold(
    similarities,
    RELATED_PERCENTILE,
    KEYWORD_PENALTY_THRESHOLD,
    RELATED_BOUNDS,
  );
}

function weightedMatchKey(score: number, weight: number): number {
  return score * weight;
}

interface ProfileMatch {
  profileItemId: string;
  score: number;
  weight: number;
  embedding: Vector;
}

function topProfileSimilarity(
  matches: readonly ProfileMatch[],
  count: number,
  secondaryMinScore: number,
): number {
  const positiveMatches = matches.filter(({ score }) => score > 0);
  if (positiveMatches.length === 0) return 0;

  const ranked = [...positiveMatches].sort(
    (left, right) =>
      weightedMatchKey(right.score, right.weight) - weightedMatchKey(left.score, left.weight) ||
      right.score - left.score ||
      left.profileItemId.localeCompare(right.profileItemId),
  );
  const strongest = ranked[0];
  if (!strongest) return 0;
  const strongestKey = weightedMatchKey(strongest.score, strongest.weight);
  const nearBest = ranked
    .filter(
      (match) =>
        weightedMatchKey(match.score, match.weight) >= strongestKey * PROFILE_MATCH_RATIO &&
        cosineSimilarity(match.embedding, strongest.embedding) >= SAME_AREA_PROFILE_COSINE,
    )
    .slice(0, count);
  const primary = weightedMean(
    nearBest.map(({ score, weight }, index) => ({
      value: score,
      weight: weight * PROFILE_MATCH_DECAY ** index,
    })),
  );
  const used = new Set(nearBest);
  const secondary = ranked.find(
    (match) =>
      !used.has(match) &&
      match.score >= secondaryMinScore &&
      cosineSimilarity(match.embedding, strongest.embedding) < SAME_AREA_PROFILE_COSINE,
  );
  if (!secondary) return primary;
  return noisyOrCoverage(primary, secondary.score);
}

/**
 * Rank candidate papers (title + abstract embeddings) against reviewer expertise.
 * Profile publications may include PDF body passages; submissions never do.
 */
export function rankPapers(
  profileItems: readonly EmbeddedProfileItem[],
  papers: readonly EmbeddedPaper[],
  options: RankingOptions = {},
): RankingResult[] {
  if (profileItems.length === 0) {
    return papers
      .map(({ paper }) => ({ paperId: paper.id, score: 0, matches: [] }))
      .sort((left, right) => left.paperId.localeCompare(right.paperId));
  }

  const profileMatchCount = Math.max(
    1,
    Math.floor(finiteNonNegative(options.profileMatchCount, DEFAULT_PROFILE_MATCH_COUNT)),
  );
  const summaryWeight = finiteNonNegative(options.summaryWeight, DEFAULT_SUMMARY_WEIGHT);
  const bodyWeight = finiteNonNegative(options.bodyWeight, DEFAULT_BODY_WEIGHT);
  const bodyMatchCount = Math.max(
    0,
    Math.floor(finiteNonNegative(options.bodyMatchCount, DEFAULT_BODY_MATCH_COUNT)),
  );
  const explanationCount = Math.max(
    0,
    Math.floor(
      finiteNonNegative(options.explanationCount, DEFAULT_EXPLANATION_COUNT),
    ),
  );

  const relatedMin = relatedThresholdForPool(
    Object.fromEntries(papers.map(({ paper, embedding }) => [paper.id, embedding])),
  );

  return papers
    .map(({ paper, embedding }) => {
      const paperEmbedding = normalizeVector(embedding);
      const itemMatches = profileItems
        .filter(({ item }) => item.weight > 0)
        .map(({ item, embedding: profileEmbedding, bodyEmbeddings = [] }) => {
          const summarySimilarity = cosineSimilarity(profileEmbedding, paperEmbedding);
          const bodySimilarity = topBodyChunkSimilarity(
            paperEmbedding,
            bodyEmbeddings,
            bodyMatchCount,
          );
          const score =
            bodySimilarity === undefined || summaryWeight + bodyWeight === 0
              ? summarySimilarity
              : (summarySimilarity * summaryWeight + bodySimilarity * bodyWeight) /
                (summaryWeight + bodyWeight);
          return {
            profileItemId: item.id,
            label: item.label,
            score,
            weight: item.weight,
            embedding: profileEmbedding,
          };
        })
        .sort(
          (left, right) =>
            right.score - left.score ||
            left.profileItemId.localeCompare(right.profileItemId),
        );

      const score = topProfileSimilarity(itemMatches, profileMatchCount, relatedMin);

      return {
        paperId: paper.id,
        score,
        matches: itemMatches
          .slice(0, explanationCount)
          .map(({ profileItemId, label, score: matchScore }) => ({
            profileItemId,
            label,
            score: matchScore,
          })),
      };
    })
    .sort(
      (left, right) =>
        right.score - left.score || left.paperId.localeCompare(right.paperId),
    );
}

interface LabeledEmbedding {
  id: string;
  embedding: Vector;
}

export const MAX_INTERESTED_PAPER_BOOST_CANDIDATES = 25;

function eligibleInterestedExamplesByPaper(
  interested: readonly LabeledEmbedding[],
  paperEmbeddings: Readonly<Record<string, Vector>>,
): ReadonlyMap<string, LabeledEmbedding[]> {
  const examplesByPaper = new Map<string, LabeledEmbedding[]>();
  for (const example of interested) {
    const threshold = relatedThresholdForSeed(example.id, example.embedding, paperEmbeddings);
    const candidates = Object.entries(paperEmbeddings)
      .filter(([paperId]) => paperId !== example.id)
      .map(([paperId, embedding]) => ({
        paperId,
        similarity: cosineSimilarity(embedding, example.embedding),
      }))
      .filter(({ similarity }) => similarity >= threshold)
      .sort(
        (left, right) =>
          right.similarity - left.similarity || left.paperId.localeCompare(right.paperId),
      )
      .slice(0, MAX_INTERESTED_PAPER_BOOST_CANDIDATES);

    if (paperEmbeddings[example.id]) examplesByPaper.set(example.id, [example]);
    for (const { paperId } of candidates) {
      const existing = examplesByPaper.get(paperId);
      if (existing) existing.push(example);
      else examplesByPaper.set(paperId, [example]);
    }
  }
  return examplesByPaper;
}

function topSimilarityNeighbors(
  embedding: Vector,
  examples: readonly LabeledEmbedding[],
  count: number,
  weight: number,
  sign: 1 | -1,
  minSimilarity = 0,
): { average: number; neighbors: FeedbackNeighbor[] } {
  if (examples.length === 0) return { average: 0, neighbors: [] };
  const ranked = examples
    .map((example) => ({
      id: example.id,
      similarity: Math.max(0, cosineSimilarity(embedding, example.embedding)),
    }))
    .filter((item) => item.similarity >= minSimilarity)
    .sort(
      (left, right) =>
        right.similarity - left.similarity || left.id.localeCompare(right.id),
    )
    .slice(0, count);
  if (ranked.length === 0) return { average: 0, neighbors: [] };
  const average =
    ranked.reduce((total, item) => total + item.similarity, 0) / ranked.length;
  return {
    average,
    neighbors: ranked.map((item) => ({
      id: item.id,
      similarity: item.similarity,
      contribution: sign * weight * (item.similarity / ranked.length),
    })),
  };
}

function labeledEmbeddings(
  decisions: Readonly<Record<string, ReviewDecision>>,
  paperEmbeddings: Readonly<Record<string, Vector>>,
  status: 'interested' | 'not_interested',
  include?: (decision: ReviewDecision) => boolean,
): LabeledEmbedding[] {
  return Object.entries(decisions).flatMap(([paperId, decision]) => {
    if (decision.status !== status) return [];
    if (include && !include(decision)) return [];
    const embedding = paperEmbeddings[paperId];
    return embedding ? [{ id: paperId, embedding }] : [];
  });
}

export const TOPIC_PRIOR_MIN_VOTES = 3;
export const TOPIC_PRIOR_WEIGHT = 0.05;

export function topicPriorsFromDecisions(
  papers: readonly Pick<Paper, 'id' | 'topics'>[],
  decisions: Readonly<Record<string, ReviewDecision>>,
): ReadonlyMap<string, number> {
  const papersById = new Map(papers.map((paper) => [paper.id, paper]));
  const stats = new Map<string, { interested: number; notInterested: number }>();
  for (const [paperId, decision] of Object.entries(decisions)) {
    if (decision.status !== 'interested' && decision.status !== 'not_interested') continue;
    const paper = papersById.get(paperId);
    if (!paper) continue;
    for (const topic of paper.topics) {
      const key = topic.trim().toLocaleLowerCase();
      if (!key) continue;
      const current = stats.get(key) ?? { interested: 0, notInterested: 0 };
      if (decision.status === 'interested') current.interested += 1;
      else current.notInterested += 1;
      stats.set(key, current);
    }
  }
  const priors = new Map<string, number>();
  for (const [key, current] of stats) {
    const total = current.interested + current.notInterested;
    if (total < TOPIC_PRIOR_MIN_VOTES) continue;
    priors.set(key, (current.interested - current.notInterested) / total);
  }
  return priors;
}

function topicBoostForPaper(
  paper: Pick<Paper, 'topics'> | undefined,
  priors: ReadonlyMap<string, number>,
): number {
  if (!paper || priors.size === 0) return 0;
  const values = paper.topics.flatMap((topic) => {
    const prior = priors.get(topic.trim().toLocaleLowerCase());
    return prior === undefined ? [] : [prior];
  });
  if (values.length === 0) return 0;
  const mean = values.reduce((total, value) => total + value, 0) / values.length;
  return Math.max(
    -TOPIC_PRIOR_WEIGHT,
    Math.min(TOPIC_PRIOR_WEIGHT, TOPIC_PRIOR_WEIGHT * mean),
  );
}

const TITLE_STOPWORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'based',
  'by',
  'for',
  'from',
  'in',
  'into',
  'of',
  'on',
  'or',
  'the',
  'to',
  'using',
  'via',
  'with',
  'within',
]);

function titleBigrams(title: string): string[] {
  const tokens = title.toLocaleLowerCase().match(/[a-z0-9]+(?:[-'][a-z0-9]+)*/g) ?? [];
  const phrases: string[] = [];
  for (let index = 0; index < tokens.length - 1; index += 1) {
    const left = tokens[index];
    const right = tokens[index + 1];
    if (!left || !right) continue;
    if (
      TITLE_STOPWORDS.has(left) ||
      TITLE_STOPWORDS.has(right) ||
      left.length <= 2 ||
      right.length <= 2
    ) {
      continue;
    }
    phrases.push(`${left} ${right}`);
  }
  return phrases;
}

/**
 * Suggests negative phrases from a paper's conference topics and content-bearing
 * title bigrams, skipping phrases the reviewer already excluded.
 */
export function proposedNegativePhrases(
  paper: Pick<Paper, 'title' | 'topics'>,
  existing: readonly string[] = [],
): string[] {
  const seen = new Set(existing.map((phrase) => phrase.trim().toLocaleLowerCase()));
  const proposals: string[] = [];
  for (const candidate of [...paper.topics, ...titleBigrams(paper.title)]) {
    const phrase = candidate.trim().toLocaleLowerCase();
    if (!phrase || seen.has(phrase)) continue;
    seen.add(phrase);
    proposals.push(phrase);
    if (proposals.length === 8) break;
  }
  return proposals;
}

export function countSimilarPapers(
  seedId: string,
  seed: Vector,
  embeddings: Readonly<Record<string, Vector>>,
  threshold?: number,
): number {
  const bar = threshold ?? variantThresholdForSeed(seedId, seed, embeddings);
  let count = 0;
  for (const [id, embedding] of Object.entries(embeddings)) {
    if (id === seedId) continue;
    if (cosineSimilarity(seed, embedding) >= bar) count += 1;
  }
  return count;
}

/**
 * Applies lightweight relevance feedback without changing the original
 * expertise score. Interested papers provide a small boost. Not-interested
 * papers only penalize close variants when the reviewer opted in, and manual
 * negative phrases remain an explicit topic-level penalty.
 */
export function applyPreferenceFeedback(
  baseRankings: readonly RankingResult[],
  paperEmbeddings: Readonly<Record<string, Vector>>,
  decisions: Readonly<Record<string, ReviewDecision>>,
  negativeKeywordEmbeddings: Readonly<Record<string, Vector>>,
  options: FeedbackOptions = {},
): RankingResult[] {
  const positiveWeight = finiteNonNegative(options.positiveWeight, 0.12);
  const negativeWeight = finiteNonNegative(options.negativeWeight, 0.28);
  const keywordWeight = finiteNonNegative(options.keywordWeight, 0.22);
  const neighborCount = Math.max(1, Math.floor(options.neighborCount ?? 3));

  const interested = labeledEmbeddings(decisions, paperEmbeddings, 'interested');
  const eligibleInterested = eligibleInterestedExamplesByPaper(interested, paperEmbeddings);
  const notInterested = labeledEmbeddings(
    decisions,
    paperEmbeddings,
    'not_interested',
    (decision) => decision.penalizeSimilar === true,
  );
  const negativeKeywords = Object.entries(negativeKeywordEmbeddings).map(([id, embedding]) => ({
    id,
    embedding,
  }));
  const keywordThresholds = new Map(
    negativeKeywords.map((keyword) => [
      keyword.id,
      keywordThresholdForPhrase(keyword.embedding, paperEmbeddings),
    ]),
  );
  const papersById = new Map((options.papers ?? []).map((paper) => [paper.id, paper]));
  const topicPriors = topicPriorsFromDecisions(options.papers ?? [], decisions);

  return baseRankings
    .map((ranking) => {
      const embedding = paperEmbeddings[ranking.paperId];
      const baseScore = ranking.baseScore ?? ranking.score;
      if (!embedding) {
        const next = { ...ranking, score: baseScore, baseScore, feedbackAdjustment: 0 };
        delete next.feedbackBreakdown;
        return next;
      }
      const interestedMatch = topSimilarityNeighbors(
        embedding,
        eligibleInterested.get(ranking.paperId) ?? [],
        neighborCount,
        positiveWeight,
        1,
      );
      const closeRejects = notInterested.filter((example) => {
        const threshold = variantThresholdForSeed(example.id, example.embedding, paperEmbeddings);
        return cosineSimilarity(embedding, example.embedding) >= threshold;
      });
      const notInterestedMatch = topSimilarityNeighbors(
        embedding,
        closeRejects,
        neighborCount,
        negativeWeight,
        -1,
      );
      const closeKeywords = negativeKeywords.filter((keyword) => {
        const threshold = keywordThresholds.get(keyword.id) ?? KEYWORD_PENALTY_THRESHOLD;
        return cosineSimilarity(embedding, keyword.embedding) >= threshold;
      });
      const keywordMatch = topSimilarityNeighbors(
        embedding,
        closeKeywords,
        neighborCount,
        keywordWeight,
        -1,
      );
      const hasDecision = decisions[ranking.paperId]?.status !== undefined;
      const topicBoost = hasDecision
        ? 0
        : topicBoostForPaper(papersById.get(ranking.paperId), topicPriors);
      const positiveBoost = positiveWeight * interestedMatch.average;
      const paperPenalty = negativeWeight * notInterestedMatch.average;
      const keywordPenalty = keywordWeight * keywordMatch.average;
      const feedbackAdjustment = positiveBoost + topicBoost - paperPenalty - keywordPenalty;
      const feedbackBreakdown: FeedbackBreakdown = {
        positiveWeight,
        negativeWeight,
        keywordWeight,
        positiveBoost,
        paperPenalty,
        keywordPenalty,
        topicBoost,
        interestedNeighbors: interestedMatch.neighbors,
        notInterestedNeighbors: notInterestedMatch.neighbors,
        keywordNeighbors: keywordMatch.neighbors,
      };
      return {
        ...ranking,
        baseScore,
        feedbackAdjustment,
        feedbackBreakdown,
        score: Math.max(-1, Math.min(1, baseScore + feedbackAdjustment)),
      };
    })
    .sort(
      (left, right) =>
        right.score - left.score || left.paperId.localeCompare(right.paperId, undefined, {
          numeric: true,
        }),
    );
}

/**
 * Returns positively boosted papers that still need a review decision, with the
 * strongest interested-paper boosts first.
 */
export function boostedUnreviewedRankings(
  rankings: readonly RankingResult[],
  decisions: Readonly<Record<string, ReviewDecision>>,
): RankingResult[] {
  return rankings
    .filter(
      (ranking) =>
        decisions[ranking.paperId]?.status === undefined &&
        (ranking.feedbackBreakdown?.positiveBoost ?? 0) > 0,
    )
    .sort(
      (left, right) =>
        (right.feedbackBreakdown?.positiveBoost ?? 0) -
          (left.feedbackBreakdown?.positiveBoost ?? 0) ||
        right.score - left.score ||
        left.paperId.localeCompare(right.paperId, undefined, { numeric: true }),
    );
}

function formatSigned(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(3)}`;
}

function truncateTitle(title: string, maxLength = 42): string {
  const normalized = title.trim().replace(/\s+/g, ' ');
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

function neighborLabel(
  neighbor: FeedbackNeighbor,
  paperId: string,
  papersById: Readonly<Record<string, Pick<Paper, 'title'>>>,
  kind: 'paper' | 'keyword',
): string {
  if (kind === 'keyword') return `“${neighbor.id}”`;
  if (neighbor.id === paperId) return 'this paper';
  const title = papersById[neighbor.id]?.title;
  return title
    ? `Paper ${neighbor.id} “${truncateTitle(title)}”`
    : `Paper ${neighbor.id}`;
}

function formatNeighborSection(
  heading: string,
  signedTotal: number,
  neighbors: readonly FeedbackNeighbor[],
  paperId: string,
  papersById: Readonly<Record<string, Pick<Paper, 'title'>>>,
  kind: 'paper' | 'keyword',
): string[] {
  if (neighbors.length === 0) return [];
  return [
    `${heading} (${formatSigned(signedTotal)}):`,
    ...neighbors.map((neighbor) => {
      const selfNote =
        kind === 'paper' && neighbor.id === paperId
          ? neighbor.contribution < 0
            ? ' (already marked not interested)'
            : ' (already marked interested)'
          : '';
      return (
        `• ${neighborLabel(neighbor, paperId, papersById, kind)}${selfNote}` +
        ` · ${neighbor.similarity.toFixed(3)} similar → ${formatSigned(neighbor.contribution)}`
      );
    }),
  ];
}

/**
 * Builds a short explanation of why preference feedback raised or lowered a
 * paper's score, naming the nearest interested, not-interested, and keyword matches.
 */
export function describePreferenceFeedback(
  ranking: Pick<RankingResult, 'paperId' | 'feedbackAdjustment' | 'feedbackBreakdown'>,
  papers: readonly Pick<Paper, 'id' | 'title'>[],
): string {
  const breakdown = ranking.feedbackBreakdown;
  const adjustment = ranking.feedbackAdjustment ?? 0;
  if (!breakdown || adjustment === 0) return '';

  const papersById = Object.fromEntries(papers.map((paper) => [paper.id, paper]));
  const headline =
    adjustment < 0
      ? `Penalized ${formatSigned(adjustment)} by preference feedback.`
      : `Boosted ${formatSigned(adjustment)} by preference feedback.`;
  const weightNote =
    `Not-interested matches use weight ${breakdown.negativeWeight.toFixed(2)}; ` +
    `interested matches use ${breakdown.positiveWeight.toFixed(2)}.`;

  return [
    headline,
    weightNote,
    ...formatNeighborSection(
      'Not interested',
      -breakdown.paperPenalty,
      breakdown.notInterestedNeighbors,
      ranking.paperId,
      papersById,
      'paper',
    ),
    ...formatNeighborSection(
      'Interested',
      breakdown.positiveBoost,
      breakdown.interestedNeighbors,
      ranking.paperId,
      papersById,
      'paper',
    ),
    ...formatNeighborSection(
      'Negative phrases',
      -breakdown.keywordPenalty,
      breakdown.keywordNeighbors,
      ranking.paperId,
      papersById,
      'keyword',
    ),
    ...(breakdown.topicBoost === 0
      ? []
      : [
          `Conference topics (${formatSigned(breakdown.topicBoost)}): vote prior from at least ${TOPIC_PRIOR_MIN_VOTES} decisions on this paper’s topics.`,
        ]),
  ].join('\n');
}

export interface PaperScorePresentation {
  caption: string;
  details: string[];
}

/**
 * Labels the number shown on a paper card so it is not mistaken for raw
 * cosine similarity when Qwen3 reranking or preference feedback moved it.
 */
export function paperScorePresentation(
  ranking: Pick<
    RankingResult,
    | 'score'
    | 'denseScore'
    | 'rerankerScore'
    | 'rerankerPairCount'
    | 'baseScore'
    | 'feedbackAdjustment'
  >,
): PaperScorePresentation {
  const adjustment = ranking.feedbackAdjustment ?? 0;
  const similarity = ranking.denseScore ?? ranking.baseScore;
  const isPureSimilarity =
    ranking.rerankerScore === undefined &&
    ranking.denseScore === undefined &&
    adjustment === 0;

  if (isPureSimilarity) {
    return { caption: 'Similarity', details: [] };
  }

  const details: string[] = [];
  if (similarity !== undefined && ranking.rerankerScore !== undefined) {
    details.push(
      `Similarity (70%): ${similarity.toFixed(3)}`,
      `Pairwise relevance (30%): ${ranking.rerankerScore.toFixed(3)}`,
    );
    if (ranking.rerankerPairCount !== undefined) {
      details.push(
        `Strongest of ${ranking.rerankerPairCount} publication${ranking.rerankerPairCount === 1 ? '' : 's'}`,
      );
    }
  } else if (similarity !== undefined) {
    details.push(`similarity ${similarity.toFixed(3)}`);
  }
  if (adjustment !== 0) {
    details.push(`feedback ${formatSigned(adjustment)}`);
  }

  if (ranking.rerankerScore !== undefined && adjustment === 0) {
    return { caption: 'Calibrated blend', details };
  }
  return { caption: 'Match score', details };
}
